import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import {
  NotificationService,
  SelectOption,
  SkeletonLoaderComponent,
  StenaButtonComponent,
  StenaSelectComponent,
} from '@app-monorepo/shared/ui/stena-components';
import { PeriodsApiService } from '../../../services/periods-api.service';
import { ReportingApiService } from '../../../services/reporting-api.service';
import { Month, PeriodMode } from '../../../models/period.model';
import {
  KpiAreaOption,
  KpiFieldRow,
  KpiFormConfig,
  ReportingCostCenter,
} from '../../../models/reporting.model';
import { ContextPickerComponent } from '../context-picker/context-picker.component';

// Shared renderer for both /reporting (mode='actual') and /budget
// (mode='budget'). Every rendering decision comes from the KpiFormConfig
// tree — the FE does not know about individual KPIs.
@Component({
  standalone: true,
  selector: 'app-kpi-form',
  templateUrl: './kpi-form.component.html',
  styleUrl: './kpi-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    ContextPickerComponent,
    SkeletonLoaderComponent,
    StenaButtonComponent,
    StenaSelectComponent,
  ],
})
export class KpiFormComponent implements OnInit {
  private readonly reportingApi = inject(ReportingApiService);
  private readonly periodsApi = inject(PeriodsApiService);
  private readonly notify = inject(NotificationService);
  private readonly fb = inject(FormBuilder);

  readonly pageIdForm = this.fb.group({
    pageId: this.fb.control<number | null>(null),
  });

  readonly mode = input.required<PeriodMode>();
  readonly title = input<string>('Reporting form');

  readonly costCenters = signal<ReportingCostCenter[]>([]);
  readonly pages = signal<KpiAreaOption[]>([]);
  readonly months = signal<Month[]>([]);

  readonly selectedCostCenterId = signal<number | null>(null);
  readonly selectedPageId = signal<number | null>(null);
  readonly selectedMonthId = signal<number | null>(null);

  readonly form = signal<KpiFormConfig | null>(null);
  readonly loading = signal(false);
  readonly loadingPages = signal(false);
  readonly saving = signal(false);

  // Budget mode shows fiscal-year marker months; actual mode shows real months.
  readonly monthOptions = computed(() => {
    const all = this.months();
    return this.mode() === 'budget'
      ? all.filter((m) => m.isFiscalYearMarker)
      : all.filter((m) => !m.isFiscalYearMarker);
  });

  readonly pageOptions = computed<SelectOption<number>[]>(() =>
    this.pages().map((p) => ({ displayName: p.name, value: p.pageId }))
  );

  readonly canShowForm = computed(
    () =>
      this.selectedCostCenterId() !== null &&
      this.selectedPageId() !== null &&
      this.selectedMonthId() !== null
  );

  readonly hasReadOnlyFields = computed(() => {
    const f = this.form();
    if (!f) return false;
    return f.rows.some((row) => row.kind === 'field' && row.readOnly);
  });

  // Fold the flat header/field row list into sections, each rendered as a
  // mat-card. A leading section without a preceding header is kept anonymous.
  readonly groupedRows = computed<
    { label: string | null; fields: KpiFieldRow[] }[]
  >(() => {
    const f = this.form();
    if (!f) return [];
    const groups: { label: string | null; fields: KpiFieldRow[] }[] = [];
    let current: { label: string | null; fields: KpiFieldRow[] } | null = null;
    for (const row of f.rows) {
      if (row.kind === 'header') {
        current = { label: row.label, fields: [] };
        groups.push(current);
      } else {
        if (!current) {
          current = { label: null, fields: [] };
          groups.push(current);
        }
        current.fields.push(row);
      }
    }
    return groups;
  });

  constructor() {
    this.pageIdForm.controls.pageId.valueChanges.subscribe((v) =>
      this.selectedPageId.set(v)
    );

    // Whenever the cost center changes, refetch the KPI-area list for it and
    // clear any stale page selection (KPI areas are per-cost-center, so an
    // old pageId is meaningless under a new cost center).
    effect(() => {
      const cc = this.selectedCostCenterId();
      this.selectedPageId.set(null);
      this.pageIdForm.controls.pageId.setValue(null, { emitEvent: false });
      this.pages.set([]);
      this.form.set(null);
      if (cc === null) return;
      this.loadingPages.set(true);
      this.reportingApi.pages(cc).subscribe({
        next: (pages) => {
          this.pages.set(pages);
          this.loadingPages.set(false);
        },
        error: () => {
          this.err('Failed to load KPI areas');
          this.loadingPages.set(false);
        },
      });
    });

    // Whenever all three selections are set, reload the form config. Clear
    // the current form immediately so the skeleton shows during refetch —
    // otherwise the previous month's values linger until the new response.
    effect(() => {
      const cc = this.selectedCostCenterId();
      const page = this.selectedPageId();
      const month = this.selectedMonthId();
      this.form.set(null);
      if (cc === null || page === null || month === null) return;
      this.reportingApi.form(cc, page, month, this.mode()).subscribe({
        next: (form) => this.form.set(form),
        error: () => this.err('Failed to load form'),
      });
    });
  }

  ngOnInit(): void {
    this.loading.set(true);
    this.reportingApi.costCenters().subscribe({
      next: (rows) => {
        this.costCenters.set(rows);
        this.periodsApi.list().subscribe({
          next: (months) => {
            this.months.set(months);
            this.loading.set(false);
          },
          error: () => {
            this.err('Failed to load months');
            this.loading.set(false);
          },
        });
      },
      error: () => {
        this.err('Failed to load cost centers');
        this.loading.set(false);
      },
    });
  }

  onSave(): void {
    const form = this.form();
    if (!form) return;
    const values: { kpiBaseId: number; value: string | null }[] = [];
    for (const row of form.rows) {
      if (row.kind !== 'field' || row.readOnly) continue;
      values.push({ kpiBaseId: row.kpiBaseId, value: row.value });
    }
    this.saving.set(true);
    this.reportingApi
      .save({
        costCenterId: form.costCenterId,
        monthId: form.monthId,
        mode: form.mode,
        values,
      })
      .subscribe({
        next: (result) => {
          this.notify.success(`Saved ${result.savedCount} values`);
          this.saving.set(false);
        },
        error: () => {
          this.err('Failed to save form');
          this.saving.set(false);
        },
      });
  }

  private err(message: string): void {
    this.notify.error(message);
  }
}
